import React, { createContext, useContext, useMemo, useState } from 'react';

// First-visit splash gate. Lifted out of App so child views can read the
// "is the welcome modal still open" signal — used today by ExplorerView's
// ?mode=scenario nudge to defer the scroll + pulse until the modal is
// dismissed (otherwise the cue plays under the splash and the user
// misses it).

const WELCOME_STORAGE_KEY = 'sfi.welcomeSeen';

const readWelcomeSeen = (): boolean => {
  try {
    return localStorage.getItem(WELCOME_STORAGE_KEY) === '1';
  } catch {
    return true;
  }
};

const writeWelcomeSeen = () => {
  try {
    localStorage.setItem(WELCOME_STORAGE_KEY, '1');
  } catch {
    // ignore quota / privacy-mode failures
  }
};

// Force-show via `?welcome=1` for booth resets / QA. Read off
// `location.search` directly so the gate doesn't depend on react-router
// being mounted at this layer.
const isForceWelcome = (): boolean => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('welcome') === '1';
};

interface WelcomeContextValue {
  // True while the splash is on screen. Children that want to defer
  // visual cues until the user has seen the splash can gate on this.
  open: boolean;
  close: () => void;
}

const WelcomeContext = createContext<WelcomeContextValue | null>(null);

export const WelcomeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Compute the gate synchronously on first render so children that read
  // `welcomeOpen` (e.g. ExplorerView's ?mode=scenario nudge) see the
  // correct value on their first commit, not a one-frame `false` that
  // a deferred effect later corrects to `true`. We're CSR-only so the
  // localStorage read happens in the browser; no SSR-flash concern.
  const [open, setOpen] = useState<boolean>(() => isForceWelcome() || !readWelcomeSeen());

  const close = () => {
    writeWelcomeSeen();
    setOpen(false);
  };

  const value = useMemo<WelcomeContextValue>(() => ({ open, close }), [open]);

  return <WelcomeContext.Provider value={value}>{children}</WelcomeContext.Provider>;
};

export const useWelcome = (): WelcomeContextValue => {
  const ctx = useContext(WelcomeContext);
  if (!ctx) {
    throw new Error('useWelcome must be used within a WelcomeProvider');
  }
  return ctx;
};
